import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Plus, Users, Search, Inbox as InboxIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useChats } from '@/hooks/useChats';
import { useAuth } from '@/hooks/useAuth';
import { Chat } from '@/types/chat';
import { formatDistanceToNow } from 'date-fns';
import { ShareModal } from '@/components/ShareModal';

const Inbox: React.FC = () => {
  const { user } = useAuth();
  const { chats, loading, getTotalUnreadCount, acceptMessageRequest, declineMessageRequest } = useChats();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);

  // Split chats into inbox and message requests
  const { inboxChats, messageRequests } = useMemo(() => {
    const inbox: Chat[] = [];
    const requests: Chat[] = [];

    chats.forEach(chat => {
      const userMembership = chat.members?.find(m => m.user_id === user?.id);
      // It's a message request if the chat is marked as such AND the user hasn't accepted
      if (chat.is_message_request && userMembership && !userMembership.accepted_at) {
        requests.push(chat);
      } else {
        inbox.push(chat);
      }
    });

    return { inboxChats: inbox, messageRequests: requests };
  }, [chats, user?.id]);

  const filteredInboxChats = useMemo(() => {
    if (!searchQuery) return inboxChats;
    const searchLower = searchQuery.toLowerCase();
    return inboxChats.filter((chat) => {
      if (chat.is_group && chat.title) {
        return chat.title.toLowerCase().includes(searchLower);
      }
      const otherMember = chat.members?.find(m => m.user_id !== user?.id);
      if (otherMember?.user) {
        return (
          otherMember.user.name?.toLowerCase().includes(searchLower) ||
          otherMember.user.username?.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  }, [inboxChats, searchQuery, user?.id]);

  const filteredRequests = useMemo(() => {
    if (!searchQuery) return messageRequests;
    const searchLower = searchQuery.toLowerCase();
    return messageRequests.filter((chat) => {
      const otherMember = chat.members?.find(m => m.user_id !== user?.id);
      if (otherMember?.user) {
        return (
          otherMember.user.name?.toLowerCase().includes(searchLower) ||
          otherMember.user.username?.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  }, [messageRequests, searchQuery, user?.id]);

  const getChatTitle = (chat: Chat) => {
    if (chat.is_group) {
      return chat.title || 'Group Chat';
    }
    const otherMember = chat.members?.find(m => m.user_id !== user?.id);
    return otherMember?.user?.name || otherMember?.user?.username || 'Unknown User';
  };

  const getChatAvatar = (chat: Chat) => {
    if (chat.is_group) return null;
    const otherMember = chat.members?.find(m => m.user_id !== user?.id);
    return otherMember?.user?.image;
  };

  const getChatAvatarFallback = (chat: Chat) => {
    if (chat.is_group) {
      return <Users className="h-4 w-4" />;
    }
    const otherMember = chat.members?.find(m => m.user_id !== user?.id);
    const name = otherMember?.user?.name || otherMember?.user?.username || 'U';
    return name.charAt(0).toUpperCase();
  };

  const getLastMessagePreview = (chat: Chat) => {
    if (!chat.last_message) return 'No messages yet';
    if (chat.last_message.type === 'share') {
      const kind = chat.last_message.share_payload?.kind;
      return `Shared a ${kind}`;
    }
    return chat.last_message.text || 'Message';
  };

  const handleAcceptRequest = async (chatId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await acceptMessageRequest(chatId);
  };

  const handleDeclineRequest = async (chatId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await declineMessageRequest(chatId);
  };

  const renderChatItem = (chat: Chat, isRequest = false) => (
    <Link
      key={chat.id}
      to={`/chat/${chat.id}`}
      className="block"
    >
      <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
        <Avatar className="h-12 w-12">
          <AvatarImage src={getChatAvatar(chat) || undefined} />
          <AvatarFallback>
            {getChatAvatarFallback(chat)}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold truncate">
                {getChatTitle(chat)}
              </h3>
              {isRequest && (
                <Badge variant="outline" className="text-xs">Request</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {chat.last_message?.created_at && (
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(chat.last_message.created_at), { addSuffix: true })}
                </span>
              )}
              {!isRequest && chat.unread_count && chat.unread_count > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {chat.unread_count}
                </Badge>
              )}
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground truncate">
            {getLastMessagePreview(chat)}
          </p>
          
          {isRequest && (
            <div className="flex gap-2 mt-2">
              <Button 
                size="sm" 
                onClick={(e) => handleAcceptRequest(chat.id, e)}
              >
                Accept
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={(e) => handleDeclineRequest(chat.id, e)}
              >
                Delete
              </Button>
            </div>
          )}
          
          {chat.is_group && !isRequest && (
            <p className="text-xs text-muted-foreground mt-1">
              {chat.members?.length || 0} members
            </p>
          )}
        </div>
      </div>
    </Link>
  );

  if (loading) {
    return (
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="container mx-auto p-6 w-full">
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">Loading chats...</div>
          </div>
        </div>
      </div>
    );
  }

  const totalUnread = inboxChats.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="container mx-auto p-6 max-w-4xl w-full">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Inbox
            </CardTitle>
            <Button onClick={() => setShowNewChatModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Chat
            </Button>
          </div>
          
          <div className="flex items-center gap-2 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="inbox" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="inbox" className="gap-2">
                <MessageCircle className="h-4 w-4" />
                Inbox
                {totalUnread > 0 && (
                  <Badge variant="destructive" className="ml-1">
                    {totalUnread}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="requests" className="gap-2">
                Requests
                {messageRequests.length > 0 && (
                  <Badge variant="outline" className="ml-1">
                    {messageRequests.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="inbox">
              {filteredInboxChats.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    {searchQuery ? 'No chats found' : 'No conversations yet'}
                  </p>
                  {!searchQuery && (
                    <Button onClick={() => setShowNewChatModal(true)}>
                      Start Your First Chat
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredInboxChats.map((chat) => renderChatItem(chat))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="requests">
              {filteredRequests.length === 0 ? (
                <div className="text-center py-8">
                  <InboxIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery ? 'No requests match your search' : 'No message requests'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Messages from people you don't follow will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredRequests.map((chat) => renderChatItem(chat, true))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ShareModal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        mode="new-chat"
      />
      </div>
    </div>
  );
};

export default Inbox;